import { Keypoint, Pose } from "@tensorflow-models/pose-detection";
import throttle from "lodash.throttle";
import { ObjectDetectionManager } from "./ObjectDetectionManager";
import { logInfo, logError, logWarning, logEvent } from "@/Utils/logger";
import { testWebGLShaderSupport } from "@/Utils/gpuHelpers";
import { retry } from "@/Utils/retry";
import {
  SHADOW_LATCH_MS,
  STANDARD_TUNING,
  type AlignmentTuning,
} from "./alignmentTuning";
import {
  CLASSIFY_TIMEOUT_MS,
  MEASURED_CYCLES,
  WARMUP_CYCLES_DISCARDED,
  beginClassification,
  finishClassification,
  recordCycle,
  recordObjectPass,
  shouldClassify,
} from "./deviceCapability";
import { PendingDetects, isDetectTimeout } from "./pendingDetects";
import {
  blindWhileVisible,
  detectorHealth,
  jointUnseenWhileFramed,
  phoneTooSlow,
} from "./liveHealth";
import {
  alignmentVideoFreeze,
  frameSignature,
  rotationVideoFreeze,
  type VideoFreezeWatch,
} from "./videoFreeze";
import {
  markModelInitFailed,
  markModelInitSucceeded,
} from "./modelInitState";
import { serverPoseService } from "./serverPoseService";
import { Events } from "@/telemetry/events";
import {
  benchmarkFrameIndex,
  cycleTimes,
  scoreBenchmark,
  type BenchmarkCycle,
  type BenchmarkResult,
} from "./benchmarkScore";
import { browserAssetDeps, loadBenchmarkAssets } from "./benchmarkFrames";
import { BENCHMARK_FRAME_HEIGHT, BENCHMARK_FRAME_WIDTH } from "./benchmarkReference";
import { benchmarkEventData } from "./benchmarkTelemetry";
import type { PersonBox } from "./personBox";

/**
 * The four joints the alignment gate tests. Hoisted out of
 * `emitJointCoordinates` so the latch and the emit share one source — two
 * copies of this list is how a joint silently stops being gated.
 */
const JOINTS_OF_INTEREST = [
  "left_wrist",
  "right_wrist",
  "left_ankle",
  "right_ankle",
];

/**
 * The query string of the URL the page loaded with, captured when this module
 * is first evaluated — `App.tsx` imports it statically, so that is always the
 * landing page. Not `window.location.search` at the time of use: every in-app
 * navigation (`navigateTo("/start-scan")`) replaces the whole URL, query
 * included, and `killDetectorAfterMs` is read when alignment starts, two
 * navigations after the page the switch was typed into.
 */
const LANDING_SEARCH = (() => {
  try {
    return window.location.search;
  } catch {
    return "";
  }
})();

/**
 * Dev-only URL switches for the benchmark proofs (spec §4.7): `forceF16`,
 * `killDetectorAfterMs`, `slowFirstDetectMs`. Always null in a prod build,
 * whatever the URL says.
 */
function devFlag(name: string): string | null {
  if (process.env.REACT_APP_IS_PROD_ENV === "true") return null;
  try {
    return new URLSearchParams(LANDING_SEARCH).get(name);
  } catch {
    return null;
  }
}

/**
 * What the camera track reports, for the frozen-camera log row. A track that
 * is `muted` or `ended` means the camera stopped delivering frames; `live` and
 * unmuted means the frames stopped somewhere after it. Read through
 * `window.activeStream`, where `useCamera` keeps the stream (HERE_BE_DRAGONS).
 */
function cameraTrackState(): {
  trackReadyState: string | null;
  trackMuted: boolean | null;
  trackEnabled: boolean | null;
} {
  try {
    const stream = (window as any).activeStream as MediaStream | null | undefined;
    const track = stream && stream.getVideoTracks ? stream.getVideoTracks()[0] : null;
    if (!track) return { trackReadyState: null, trackMuted: null, trackEnabled: null };
    return {
      trackReadyState: track.readyState,
      trackMuted: track.muted,
      trackEnabled: track.enabled,
    };
  } catch {
    return { trackReadyState: null, trackMuted: null, trackEnabled: null };
  }
}

/** Page-load state for `?slowFirstDetectMs`; the manager is a singleton. */
let slowFirstDetect: { gate: Promise<void>; done: boolean } | null = null;

/**
 * Dev proof (`?slowFirstDetectMs=N`): a cold phone on a warm one. From the
 * first detect, everything sent to the pose worker waits N ms, as it does
 * behind a real first-detection shader compile (prod iPhones: a median 14 s on
 * a first visit). Each request's time limit still runs from the moment it is
 * created, as it does for real. `null` once the wait is over, and always in a
 * prod build.
 */
function devSlowFirstDetectGate(): Promise<void> | null {
  if (slowFirstDetect) return slowFirstDetect.done ? null : slowFirstDetect.gate;
  const ms = Number(devFlag("slowFirstDetectMs"));
  const state = { gate: Promise.resolve(), done: !(ms > 0) };
  if (!state.done) {
    state.gate = new Promise<void>((resolve) => {
      setTimeout(() => {
        state.done = true;
        resolve();
      }, ms);
    });
  }
  slowFirstDetect = state;
  return state.done ? null : state.gate;
}

/**
 * A gap longer than this between emits means no pose was detected at all,
 * rather than that the joints moved. Mirrors `POSE_LOST_GAP_MS` in
 * `src/telemetry/alignmentAccumulator.ts` so the gate and the telemetry agree
 * on what "we could not see them" means.
 */
const POSE_LOST_GAP_MS = 500;

/**
 * How long a single SCAN detect may go unanswered before its caller is failed.
 *
 * Matching responses by id means a lost worker message settles nothing, and
 * `useDetectionEffects` awaits each `processFrame` before requesting the next
 * animation frame — so without a bound, one dropped message wedges the alignment
 * loop for the rest of the session.
 *
 * 5 s bounds a stall on a WARM detector: the slowest measured devices in
 * BUG-0008 run one cycle in ~340 ms, so a scan detect at 5 s is a lost message,
 * not a slow phone. It is NOT a bound on a cold one — see
 * `BOOT_DETECT_TIMEOUT_MS`, which the warm-up and the startup check use instead.
 */
export const DETECT_TIMEOUT_MS = 5000;

/**
 * The limit for the detects that run before any scan: the model warm-up and
 * the startup check. Their timeouts never count as a broken detector.
 *
 * A phone's first detection compiles the model's WebGL shaders, and that is
 * far slower than any scan detect. Prod, 2026-09-12/13: a median 14 s on iPhone
 * first visits (95% of 97 over 5 s, 90% under 33 s) and 3 s on Android first
 * visits, against 0.5 s on return visits. Under the 5 s scan limit, every
 * tester's first visit on the 2026-09-14 dev build tripped "detector broken"
 * before the scan started and was moved to the cloud for the whole page load.
 *
 * Still a bound, because the warm-up gates the Start button and a lost message
 * would otherwise hold it forever. A timeout here costs a warm-up retry, which
 * queues behind the compile still running in the worker and answers when it
 * finishes.
 */
export const BOOT_DETECT_TIMEOUT_MS = 60000;

/**
 * the Pose detector is reversing the joints and since we only use the
 * areas to make sure the scanner is standing in the correct posture I think
 * it's fine to reverse the boundaries as well
 *
 * ── `inflate`: which edges may move on the low-end path (BUG-0008) ────────
 *
 * These four rects ARE the pose. Their CENTRES encode where a body part has to
 * be; their SIZE is the tolerance. On the low-end path the tolerance grows by
 * `tuning.boxInflateRatio`, and `inflate` says per edge whether that edge is
 * allowed to move (1) or is pinned (0).
 *
 * The two pinned cases are the whole safety argument against the backend's
 * requirement, and neither is arbitrary:
 *
 *   - **Wrist `maxY` is pinned.** y=290 of a 1336-tall reference is the app's
 *     only encoding of "the arms are up". The reconstruction network is trained
 *     on silhouettes of poses sampled uniformly between `CACTUS_POSE_WIDE` and
 *     `CACTUS_POSE_BENT` (bodyscanner: pipeline/synthetic/generator_full.py:248-252),
 *     which spans arms-straight to elbows-fully-bent and legs-together to
 *     legs-apart — but the shoulder abduction term is constant across the whole
 *     envelope. Arms-down is out of distribution, and there is NO runtime pose
 *     validator on the PWA path, so it would produce a wrong avatar rather than
 *     an error.
 *   - **The ankles' inner X edges are pinned.** The gap between the two ankle
 *     boxes is 85.5 reference px, the tightest clearance in the layout, and it
 *     is what keeps the legs separated in the silhouette. Outward-only costs
 *     nothing worth having and removes the only way inflation could merge them.
 */
const boundaries: Boundary[] = [
  {
    name: "right_wrist",
    minX: 165,
    maxX: 316,
    minY: 90,
    maxY: 290,
    src: "/images/hand-green.png",
    src2: "/images/hand-red.png",
    inflate: { minX: 1, maxX: 1, minY: 1, maxY: 0 },
  }, // Right Wrist
  {
    name: "left_wrist",
    minX: 712,
    maxX: 860,
    minY: 90,
    maxY: 290,
    src: "/images/hand-green.png",
    src2: "/images/hand-red.png",
    inflate: { minX: 1, maxX: 1, minY: 1, maxY: 0 },
  }, // Left Wrist
  {
    name: "right_ankle",
    minX: 300,
    maxX: 468.5,
    minY: 1006.5,
    maxY: 1262,
    src: "/images/left-leg-green.png",
    src2: "/images/left-leg-red.png",
    inflate: { minX: 1, maxX: 0, minY: 1, maxY: 1 },
  }, // Right Ankle
  {
    name: "left_ankle",
    minX: 554,
    maxX: 722.5,
    minY: 1006.5,
    maxY: 1262,
    src: "/images/right-leg-green.png",
    src2: "/images/right-leg-red.png",
    inflate: { minX: 0, maxX: 1, minY: 1, maxY: 1 },
  }, // Left Ankle
];
/** Per-edge inflation permission: 1 = may move outward, 0 = pinned. */
type InflateMask = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};
type Boundary = {
  name: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  src: string;
  src2: string;
  inflate: InflateMask;
};

type JointArea = {
  boundary: Boundary;
  center: { x: number; y: number };
  isJointInArea: boolean;
  imageSrc: string;
  /**
   * The joint is inside the INFLATED box but would have been outside the
   * original one — i.e. the low-end inflation is what let it pass.
   *
   * This is the measurement that decides whether the inflation earns its risk.
   * `jointOutOfAreaMs` records how LONG a joint was outside its box but not how
   * FAR, so nothing today can say whether 12% is doing any work. Counted into
   * `scan.alignment.summary.inflationRescuedTicks`; if it comes back near zero
   * across a hundred sessions, the latch is carrying the fix and the inflation
   * — the riskiest of the three relaxations for avatar quality — should go.
   *
   * Always false on the standard path.
   */
  rescuedByInflation: boolean;
};

type JointCoordinates = {
  [key: string]: {
    x: number;
    y: number;
    isDetected: boolean;
    /**
     * The keypoint's own confidence, unrounded — the number `keyPointInFrame`
     * compares against its 0.3 threshold. Carried for telemetry only; nothing
     * in the gate, the guidance or the overlay reads it.
     */
    score?: number;
    area?: JointArea | null;
  } | null;
};

type EventCallback<T> = (data: T) => void;

type GuidanceMessage =
  | "Move Back"
  | "Move Closer"
  | "Tilt Device Up"
  | "Tilt Device Down"
  | null
  | undefined;

/** The frames the scan analyses stopped changing (`videoFreeze.ts`). */
export type CameraFrozenEvent = {
  phase: "alignment" | "rotation";
  frozenMs: number;
};

export type RotationPoseEvent = {
  poses: Pose[];
  frame: ImageData;
  ticksCount: number;
  /**
   * The spin this detect was SENT in (`beginRotationRun`). A reply that lands
   * after its spin failed carries an old run and must be dropped.
   */
  run: number;
};

type EventMap = {
  guidance: GuidanceMessage;
  ready: boolean;
  error: Error;
  jointCoordinates: JointCoordinates;
  correctPosture: boolean;
  rotationPose: RotationPoseEvent;
  cameraFrozen: CameraFrozenEvent;
  /**
   * An alignment frame finished pose detection, on the phone or the cloud,
   * whether or not it found anyone: every frame the loop completed, where
   * `jointCoordinates` is only the frames with a person box AND a pose.
   * Emitted after that frame's `jointCoordinates`, if it had one — the
   * accumulator relies on the order to count each phone frame once.
   */
  frameDetected: "device" | "server";
};

export class PoseDetectionManager {
  private static instance: PoseDetectionManager;
  private worker: Worker | null = null;
  /**
   * Outstanding worker detects, matched to their responses by request id.
   * Replaces the previous listener-per-call design, in which concurrent detects
   * cross-resolved — see `pendingDetects.ts` and `HERE_BE_DRAGONS.md`.
   */
  private pendingDetects = new PendingDetects<Pose[]>();
  isRunning = false;
  private isInitialized = false;
  private isWarmedUp = false;
  private initCalled = false;
  private objectDetectionManager = ObjectDetectionManager.getInstance();
  private pose: Pose | null = null; // Smoothed pose data
  private posesBuffer: Pose[] = [];
  private heightRatio: number = 0;
  /** When `heightRatio` was last read, so a stale reading cannot count as "framed". */
  private heightRatioAt: number | null = null;
  /** Dev-only `?killDetectorAfterMs`: armed once per page load. */
  private killDetectorTimer: ReturnType<typeof setTimeout> | null = null;
  private frameHeight: number = 0;
  private frameWidth: number = 0;
  private framesProcessed = 0;
  private canvasDimensions: { width: number; height: number } | null = null;
  private objectDetectionWarmedUp = false;

  /**
   * When each joint was last seen INSIDE its target box AND confidently
   * detected. BUG-0008: the old gate required all four in the same emit, which
   * on the affected devices is once per ~150-340ms — ordinary postural sway
   * carries a joint out between samples and the four never coincide, so the
   * boxes flicker and the scan never starts.
   *
   * `tuning.jointLatchMs` is 0 on the standard path, which makes
   * `now - stamp <= 0` true for exactly the joints stamped in THIS emit — i.e.
   * the old `areasInPosition.every(...)`, with different syntax.
   *
   * See docs/superpowers/plans/2026-09-02-bug-0008-low-end-device-alignment-fix-plan.md §3.2.
   */
  private lastInAreaAt: Record<string, number> = {};
  /**
   * Emit timestamp, for detecting a pose dropout that must void the latch.
   *
   * `null`, not 0: `performance.now()` can legitimately return 0, and a
   * sentinel that collides with a real reading makes the dropout check
   * unreachable for the whole session that starts at time zero.
   */
  private lastEmitAt: number | null = null;
  /**
   * Whether today's exact, un-latched, un-inflated gate was satisfied at least
   * once. Read by telemetry so the residual quality cost of the assisted path
   * is measurable per scan rather than assumed away — the backend has no
   * runtime pose validator, so a bad pose produces a wrong avatar, not an
   * error. Plan §4.4.
   */
  private strictPostureSeen = false;

  /**
   * Emits a `SHADOW_LATCH_MS` latch WOULD have converted from false to true,
   * had one been applied. It is not applied: the emitted gate below is
   * unchanged from 2.5.13.
   *
   * This is the counterfactual the release exists to collect. The per-joint
   * latch is the one part of the BUG-0008 fix with no evidence behind it —
   * `correctPostureLostCount` already measures how often the gate drops, and
   * `heightRatioP50` already measures the guidance dead zone, but nothing could
   * say whether a latch would have caught those drops without shipping one.
   * Near-zero across many journeys is a decisive signal that the latch is NOT
   * the mechanism; a large count is encouraging rather than conclusive, because
   * a session can have many rescued emits and still never pass the gate.
   *
   * Same lifecycle as `strictPostureSeen`: survives `pause()` (which runs before
   * the summary is emitted) and is cleared by `resetAttemptMetrics()`.
   */
  private latchRescuedEmits = 0;

  private tuning: AlignmentTuning = STANDARD_TUNING;
  /** One re-arm only, so a tab toggled repeatedly does not stack listeners. */
  private classifyRearmed = false;
  /** From the worker's INIT_SUCCESS: the TF.js backend, and whether it runs float32. */
  private workerBackend: string | null = null;
  private float32Enabled: boolean | null = null;
  /** Which path the last `detectPose` took — live health reads the device path only. */
  private lastDetectSource: "device" | "server" = "device";
  /** The current spin; see `beginRotationRun`. */
  private rotationRun = 0;

  // Integrated event handling
  private listeners = new Map<keyof EventMap, Set<EventCallback<any>>>();

  private constructor() {}

  static getInstance(): PoseDetectionManager {
    if (!PoseDetectionManager.instance) {
      PoseDetectionManager.instance = new PoseDetectionManager();
    }
    return PoseDetectionManager.instance;
  }

  // Event methods integrated directly into manager class
  on<K extends keyof EventMap>(
    event: K,
    callback: EventCallback<EventMap[K]>
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);
    return () => this.off(event, callback);
  }

  off<K extends keyof EventMap>(
    event: K,
    callback: EventCallback<EventMap[K]>
  ): void {
    this.listeners.get(event)?.delete(callback);
    if (this.listeners.get(event)?.size === 0) {
      this.listeners.delete(event);
    }
  }

  private emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for ${String(event)}:`, error);
      }
    });
  }

  removeAllListeners<K extends keyof EventMap>(event?: K): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /** Size of the last frame handed to the model, in pixels. 0 until one is. */
  private lastFrameWidth = 0;
  private lastFrameHeight = 0;

  /**
   * The pose input's pixel size, for the alignment summary. Read once at phase
   * exit, never in the hot path.
   */
  getPoseFrameSize(): { width: number; height: number } {
    return { width: this.lastFrameWidth, height: this.lastFrameHeight };
  }

  async initialize(model: string = "MoveNet"): Promise<void> {
    if (this.isInitialized || this.initCalled) {
      logInfo(
        "PoseDetectionManager initialization skipped: already initialized or in progress."
      );
      return;
    }
    this.initCalled = true;
    logInfo("PoseDetectionManager initialization started.");

    // Fire-and-forget, and deliberately BEFORE the model attempt rather than
    // after it succeeds. The population server-side pose recovers outright is
    // the one whose model init fails terminally — gating this on that success
    // would arrange for it to be unavailable to exactly the users it is for.
    // It never throws and never blocks: every failure inside leaves the device
    // path in force.
    void serverPoseService.initialize();

    const initAttempt = async () => {
      // Ensure cleanup of any previous failed attempt
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }

      // Anything still waiting on the PREVIOUS worker can never be answered by
      // it — this branch runs on a retry after a failed attempt, and the old
      // worker was just terminated. Fail them now rather than letting each one
      // burn a full DETECT_TIMEOUT_MS.
      this.pendingDetects.clear("Pose worker was re-initialized");

      this.worker = new Worker(
        new URL("@/workers/poseDetectionWorker.ts", import.meta.url),
        { type: "module" }
      );

      // ONE dispatcher for the life of this worker, installed before any DETECT
      // can be posted. The previous design attached a listener per detect call
      // to a worker that broadcasts to all listeners, so two in-flight detects
      // resolved each other's promises. INIT_*/other message types fall through
      // untouched, which is what lets the scoped INIT handshake handler below
      // keep working unchanged.
      this.worker.addEventListener("message", this.handleDetectMessage);
      // A crashed worker never answers. Before this, nothing listened and the
      // scan just stopped responding (spec §1, item 4).
      this.worker.addEventListener("error", this.handleWorkerError);

      const backend = testWebGLShaderSupport() ? "webgl" : "cpu";
      logInfo(`Initializing worker with backend: ${backend}`);

      this.worker.postMessage({
        type: "INIT",
        payload: { selectedModel: model, backend, forceF16: devFlag("forceF16") === "1" },
      });

      await new Promise<void>((resolve, reject) => {
        if (!this.worker) {
          logError("Worker is null after creation.", {
            source: "PoseDetectionManager",
          });
          return reject(new Error("Worker is null after creation."));
        }

        const handler = async (e: MessageEvent) => {
          const { type, error } = e.data;

          // Crucially, remove the listener once a final message is received.
          if (type === "INIT_SUCCESS" || type === "INIT_ERROR") {
            this.worker?.removeEventListener("message", handler);
          }

          if (type === "INIT_ERROR") {
            const err =
              typeof error === "string"
                ? error
                : error?.message
                ? error?.message
                : "Unknown error";
            logError("Worker initialization failed.", {
              source: "PoseDetectionManager",
              errorMessage: err || "Unknown error",
            });
            reject(new Error(err));
          } else if (type === "INIT_SUCCESS") {
            this.workerBackend = typeof e.data.backend === "string" ? e.data.backend : null;
            this.float32Enabled =
              typeof e.data.float32Enabled === "boolean" ? e.data.float32Enabled : null;
            logInfo("Worker initialized successfully. Warming up model...");
            try {
              await this.objectDetectionManager.initialize(); // no need to warp it in retry
              this.objectDetectionWarmedUp = true;
              await this.warmupModel();
              logInfo("Model warmed up and object detection initialized.");
              resolve();
            } catch (warmupError) {
              logError(
                "Model warmup or object detection initialization failed.",
                // @ts-ignore
                { errorMessage: warmupError?.message || "Unknown error" }
              );
              reject(warmupError);
            }
          }
        };

        this.worker.addEventListener("message", handler);
      });
    };

    try {
      await retry(initAttempt, {
        retries: 3,
        delayMs: 1000,
        onRetry: (attempt, error) => {
          logWarning(`Initialization attempt ${attempt} failed. Retrying...`, {
            // @ts-ignore
            errorMessage: error?.message || "Unknown error",
          });
        },
      });

      this.isInitialized = true;
      // Clears any earlier terminal failure in this page load. `initialize()` is
      // re-enterable (`initCalled` is reset in the catch below), and a device
      // that recovered must not stay routed to the server — on-device is the
      // measured-better path whenever it works.
      markModelInitSucceeded();
      logInfo("PoseDetectionManager initialization completed successfully.");

      // BUG-0008. Deliberately LAST, and deliberately NOT awaited:
      //
      //  - after `isInitialized`, so `isReady()` is already true and nothing
      //    that gates the Start button on it can be delayed by the measurement;
      //  - after warmup's `emit("ready")`, so `modelsLoading` is already false;
      //  - not awaited, so `initialize()` resolves immediately and this is a
      //    genuine background test rather than up to 6 s in front of the user
      //    on exactly the slow devices it exists to identify;
      //  - outside warmup's `retry`, so a measurement failure can never fail
      //    initialization — it leaves the class `unknown`, i.e. today's path.
      //
      // It yields to a real scan (see the `isRunning` check in the loop) so the
      // measurement never competes with the scan for the worker and the CPU.
      void this.classifyDeviceCapability();
    } catch (error) {
      this.initCalled = false;
      this.isInitialized = false;
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }
      // The terminated worker will never answer; fail its callers now instead
      // of leaving each to burn a full DETECT_TIMEOUT_MS.
      this.pendingDetects.clear("Pose worker initialization failed");
      // THE terminal point — the outer retry is exhausted. Deliberately here and
      // nowhere else: the inner failures (`ObjectDetectionManager initialization
      // failed after all retries.`, `Model warmup failed after all retries.`)
      // are usually rescued by a later outer attempt, and marking on those
      // overstates the population ~5x, which is the trap BUG-0004's signature
      // was written around.
      markModelInitFailed();
      logError(
        "PoseDetectionManager initialization failed after all retries.",
        // @ts-ignore
        { errorMessage: error?.message || "Unknown error" }
      );
      this.emit("error", error as Error);
      throw error;
    }
  }

  /**
   * A method, not an arrow property, so the boot-detect tests can reach it
   * through the prototype; it is only ever called as `this.warmupModel()`.
   */
  private async warmupModel(): Promise<void> {
    if (this.isWarmedUp) {
      logInfo("PoseDetectionManager model already warmed up.");
      return;
    }
    // The worker is initialized at this point, but the manager is not fully initialized until warmup is complete.
    // So, we only check for the worker's existence.
    if (!this.worker) {
      throw new Error("Worker not initialized for warmup");
    }
    logInfo("Warming up PoseDetectionManager model...");

    const warmupAttempt = async () => {
      // Create a small blank canvas to use as a warmup image
      // Safari/iOS compatibility: Use OffscreenCanvas if available, otherwise fall back to HTMLCanvasElement
      let canvas: OffscreenCanvas | HTMLCanvasElement;
      if (typeof OffscreenCanvas !== 'undefined') {
        canvas = new OffscreenCanvas(640, 480);
      } else {
        canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 480;
      }
      const ctx = canvas.getContext("2d") as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
      if (ctx) {
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);

      if (!imageData) {
        throw new Error("Failed to create warmup image");
      }

      // Run detection once to warm up the model — on the PHONE, and as a boot
      // detect. Never through `detectPose`: that asks Auto where the frame
      // goes, and after a slow first detection Auto said "detector broke, use
      // the cloud", so the retry "succeeded" on the cloud while the phone was
      // still compiling, and the session was moved there before any scan.
      await this.detectOnDevice(imageData, { boot: true });
    };

    try {
      await retry(warmupAttempt, {
        retries: 3,
        delayMs: 500,
        // Not after a timeout. At the boot limit the worker is stuck, and a
        // retry would only queue behind it for another 60 s, three times, before
        // this init attempt fails and the outer retry starts a fresh worker. A
        // hung phone would reach the models-failed rescue after ~16 minutes;
        // without the retries it takes ~4 (four init attempts at 60 s), against
        // ~1.5 under the old 5 s limit and never on a build with no limit. An
        // error still retries: the worker answered.
        retryCondition: (error) => !isDetectTimeout(error),
        onRetry: (attempt, error) => {
          logWarning(`Model warmup attempt ${attempt} failed. Retrying...`, {
            // @ts-ignore
            errorMessage: error?.message || "Unknown error",
          });
        },
      });
      this.isWarmedUp = true;
      this.emit("ready", true);
      logInfo("PoseDetectionManager model warmed up successfully.");
    } catch (error) {
      logError("Model warmup failed after all retries.", {
        // @ts-ignore
        errorMessage: error?.message || "Unknown error",
      });
      this.emit("error", new Error("Model warmup failed"));
      throw error;
    }
  }

  /**
   * Times `WARMUP_CYCLES_DISCARDED + MEASURED_CYCLES` detect cycles on the
   * shipped real frames (a black frame when they cannot load) and scores the
   * keypoints against the reference, to decide which path this device gets.
   *
   * Three properties worth keeping if this is ever rewritten:
   *
   * 1. **It runs on the model-warmup path**, so it needs no camera and no
   *    permission, and it completes while the user is reading the
   *    before-you-start / how-it-works screens. The user never waits for it.
   * 2. **It reproduces the real loop's shape**, object-detection stride
   *    included, because the number that matters is how long a REPRESENTATIVE
   *    cycle takes — the EfficientDet pass runs on the main thread and is the
   *    largest non-worker cost in the alignment loop.
   * 3. **It never runs on a hidden tab.** A throttled tab measures the OS, not
   *    the handset. If hidden, it re-arms once on the next `visible`.
   */
  private classifyDeviceCapability = async (): Promise<void> => {
    if (!shouldClassify()) {
      // Hidden tab (or already decided for this session). Re-arm once.
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible" &&
        !this.classifyRearmed
      ) {
        this.classifyRearmed = true;
        const onVisible = () => {
          document.removeEventListener("visibilitychange", onVisible);
          // Never compete with a live scan for the worker.
          if (!this.isRunning) void this.classifyDeviceCapability();
        };
        document.addEventListener("visibilitychange", onVisible);
      }
      return;
    }

    try {
      // Real frames when the asset loads and its reference matches this model;
      // otherwise today's black frame, so the speed class never goes missing
      // (spec §4.3). The loader never throws.
      const loaded = await loadBenchmarkAssets(browserAssetDeps);
      const assets = loaded.ok ? loaded.assets : null;
      const width = assets ? BENCHMARK_FRAME_WIDTH : 640;
      const height = assets ? BENCHMARK_FRAME_HEIGHT : 480;

      let canvas: OffscreenCanvas | HTMLCanvasElement;
      if (typeof OffscreenCanvas !== "undefined") {
        canvas = new OffscreenCanvas(width, height);
      } else {
        // Safari/iOS: no OffscreenCanvas. Same fallback as warmupAttempt.
        canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
      }
      // `willReadFrequently`, like every real read path in the app
      // (`useProcessVideoFrame`, `CountdownManager`). Without the hint a
      // Chromium canvas stays GPU-backed and each `getImageData` inside the
      // timed window forces a GPU→CPU readback the production loop never pays.
      const ctx = canvas.getContext("2d", { willReadFrequently: true }) as
        | OffscreenCanvasRenderingContext2D
        | CanvasRenderingContext2D
        | null;
      if (!ctx) {
        // iOS under memory pressure, or an in-app WKWebView, can refuse a 2D
        // context. `unknown` maps to the standard path — but say so in the logs.
        finishClassification();
        logWarning("[PoseDetection] device capability measurement failed", {
          errorMessage: "2D context unavailable",
        });
        return;
      }
      if (!assets) {
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, width, height);
      }

      const run = await this.runBenchmarkCycles(
        ctx,
        width,
        height,
        assets ? assets.images : null,
        { recordTiming: true }
      );
      // The decoded frames live only in `loaded`, a local; nothing holds them
      // past this function, so they are released when the check ends (spec §5).
      const benchmark = scoreBenchmark({
        reference: assets ? assets.reference : null,
        unknownReason: run.yielded ? "yielded" : loaded.ok ? null : loaded.reason,
        cycles: run.cycles,
      });
      const metrics = finishClassification(run.yielded ? "yielded" : undefined, benchmark);
      logInfo("[PoseDetection] device capability measured", {
        deviceClass: metrics.deviceClass,
        cycleMedianMs: metrics.cycleMedianMs,
        cycleMaxMs: metrics.cycleMaxMs,
        objectPassMs: metrics.objectPassMs,
        source: metrics.source,
        benchmarkVerdict: benchmark.verdict,
      });
      void logEvent(Events.DEVICE_BENCHMARK, "[telemetry] device benchmark", {
        data: benchmarkEventData(benchmark, metrics, {
          referenceVersion: assets ? assets.reference.version : null,
          backend: this.workerBackend,
          float32Enabled: this.float32Enabled,
          forcedF16: devFlag("forceF16") === "1",
        }),
      });
    } catch (error) {
      // `unknown` is the fail-safe and maps to the standard path.
      finishClassification(
        undefined,
        scoreBenchmark({ reference: null, unknownReason: "error", cycles: [] })
      );
      logWarning("[PoseDetection] device capability measurement failed", {
        // @ts-ignore
        errorMessage: error?.message || "Unknown error",
      });
    }
  };

  /**
   * The timed loop, shared by the startup check and the dev calibration page
   * (Task 14). `recordTiming` feeds `deviceCapability`'s classifier; a
   * calibration run leaves the session verdict alone.
   *
   * Same shape as the real loop — the object pass on the stride, a fresh
   * `ImageData` per cycle (detects transfer the buffer), the scan yielding
   * the measurement the moment it starts.
   */
  private async runBenchmarkCycles(
    ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
    width: number,
    height: number,
    images: CanvasImageSource[] | null,
    opts: { recordTiming: boolean }
  ): Promise<{ cycles: BenchmarkCycle[]; yielded: boolean }> {
    const total = WARMUP_CYCLES_DISCARDED + MEASURED_CYCLES;
    // One clock for the budget and the verdict's `elapsed` (see
    // `beginClassification`). Re-stamped once the discarded cycles are done,
    // below: they absorb any first-run compile, so they are not the budget's.
    let runStartedAt = opts.recordTiming ? beginClassification() : performance.now();
    const cycles: BenchmarkCycle[] = [];
    let yielded = false;

    for (let i = 0; i < total; i++) {
      // Yield to a real scan: it needs the worker and the CPU, and a user
      // tapping Start says nothing about the handset.
      if (this.isRunning) {
        yielded = true;
        break;
      }
      // The budget is for the MEASURED cycles only. Checked from the first of
      // them: before that, `runStartedAt` still includes the discarded cycles,
      // and one cold first detection (median 14 s on prod iPhone first visits)
      // would end the run as a timeout — "slow phone", cached for the session.
      if (
        i >= WARMUP_CYCLES_DISCARDED &&
        performance.now() - runStartedAt >= CLASSIFY_TIMEOUT_MS
      ) {
        break;
      }

      const startedAt = performance.now();
      const frameIndex = images ? benchmarkFrameIndex(i) : 0;
      if (images) ctx.drawImage(images[frameIndex], 0, 0, width, height);
      const frame = ctx.getImageData(0, 0, width, height);

      let personBox: PersonBox | null = null;
      let objectPassRan = false;
      if (i % STANDARD_TUNING.objectDetectionStride === 0) {
        const objStartedAt = performance.now();
        try {
          if (!this.objectDetectionManager.isRunning) {
            this.objectDetectionManager.start();
          }
          // `processFrame` is throttled with `leading: true`; reset it so this
          // pass is a leading edge and not a cached ~0 ms result.
          (this.objectDetectionManager.processFrame as any).cancel?.();
          const res = await this.objectDetectionManager.processFrame(
            frame,
            performance.now()
          );
          personBox = res ? res.personBox : null;
          objectPassRan = true;
        } catch {
          // MediaPipe's GPU delegate fails on Safari (TRAPS.md): a pose-only
          // measurement is the honest one there.
        }
        if (this.isRunning) {
          yielded = true;
          break;
        }
        if (opts.recordTiming) recordObjectPass(performance.now() - objStartedAt);
      }

      // The device path explicitly — this measures THE HANDSET, never the
      // server. Fresh on real frames, so it is comparable with the reference.
      // A boot detect: it runs before any scan, so a slow one is not a broken
      // detector (`BOOT_DETECT_TIMEOUT_MS`).
      let poses: Pose[];
      try {
        poses = await this.detectOnDevice(frame, { fresh: !!images, boot: true });
      } catch (error) {
        // A run that fails before a single measured cycle is not a speed
        // verdict, however long the compile before it took. Re-stamp so
        // `finishClassification` reads `error`, not `timeout`.
        if (opts.recordTiming && i < WARMUP_CYCLES_DISCARDED) beginClassification();
        throw error;
      }
      if (this.isRunning) {
        yielded = true;
        break;
      }
      const cycleMs = performance.now() - startedAt;
      if (opts.recordTiming) recordCycle(cycleMs);
      cycles.push({
        frameIndex,
        keypoints: poses && poses.length ? poses[0].keypoints : null,
        personBox,
        objectPassRan,
        cycleMs,
      });

      // The 6 s budget is for the MEASURED cycles. The discarded ones exist to
      // absorb first-run costs, and on a cold phone those are not a speed:
      // prod iPhones spend a median 14 s on their first detection and then run
      // 9-38 ms cycles. Counting it put healthy phones past the budget, where
      // `finishClassification` calls them `marginal` ("slow phone").
      if (i === WARMUP_CYCLES_DISCARDED - 1) {
        runStartedAt = opts.recordTiming ? beginClassification() : performance.now();
      }
    }
    return { cycles, yielded };
  }

  /**
   * Dev-only calibration for the /dev/benchmark page: the startup check's exact
   * loop and scoring, without touching this session's device verdict. Throws
   * when the assets do not load — calibrating against nothing is meaningless.
   */
  async runBenchmarkForCalibration(): Promise<{
    result: BenchmarkResult;
    cycleMedianMs: number | null;
    cycleMaxMs: number | null;
    backend: string | null;
    float32Enabled: boolean | null;
    forcedF16: boolean;
    referenceVersion: number;
  }> {
    if (!this.isReady()) throw new Error("Pose detection is not ready yet");
    const loaded = await loadBenchmarkAssets(browserAssetDeps);
    if (!loaded.ok) throw new Error("Benchmark assets unavailable: " + loaded.reason);

    const canvas = document.createElement("canvas");
    canvas.width = BENCHMARK_FRAME_WIDTH;
    canvas.height = BENCHMARK_FRAME_HEIGHT;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("2D context unavailable");

    const run = await this.runBenchmarkCycles(
      ctx,
      BENCHMARK_FRAME_WIDTH,
      BENCHMARK_FRAME_HEIGHT,
      loaded.assets.images,
      { recordTiming: false }
    );
    const result = scoreBenchmark({
      reference: loaded.assets.reference,
      unknownReason: run.yielded ? "yielded" : null,
      cycles: run.cycles,
    });
    const times = cycleTimes(run.cycles);
    return {
      result,
      cycleMedianMs: times.cycleMedianMs,
      cycleMaxMs: times.cycleMaxMs,
      backend: this.workerBackend,
      float32Enabled: this.float32Enabled,
      forcedF16: devFlag("forceF16") === "1",
      referenceVersion: loaded.assets.reference.version,
    };
  }

  private handleSettingPose(pose: Pose): void {
    if (this.posesBuffer.length > 5) {
      this.posesBuffer.shift();
    }
    this.posesBuffer.push(pose);
    this.pose = this.getSmoothedPose();

    // Emit joint coordinates for the canvas
    if (this.pose) {
      this.emitJointCoordinates(this.pose);
    }
  }

  private getSmoothedPose(): Pose | null {
    const bufferSize = this.posesBuffer.length;
    if (bufferSize === 0) return null;

    // Get the structure of keypoints from the first pose
    const keypoints = this.posesBuffer[0].keypoints;

    // Create the smoothed pose
    const smoothedPose: Pose = {
      score: 0,
      keypoints: keypoints.map((kp) => {
        // For each keypoint name/position, calculate the average across all poses
        const keypointsByName = this.posesBuffer.map(
          (pose) => pose.keypoints.find((p) => p.name === kp.name)!
        );

        const count = keypointsByName.length || 1; // Avoid division by zero

        const sumX = keypointsByName.reduce((sum, k) => sum + k.x, 0);
        const sumY = keypointsByName.reduce((sum, k) => sum + k.y, 0);
        const sumScore = keypointsByName.reduce(
          (sum, k) => sum + (k.score || 0),
          0
        );

        // Return the averaged keypoint
        return {
          name: kp.name,
          x: sumX / count,
          y: sumY / count,
          score: sumScore / count,
        };
      }),
    };

    // Calculate the overall pose score as the average of keypoint scores
    smoothedPose.score =
      smoothedPose.keypoints.reduce((sum, kp) => sum + (kp.score || 0), 0) /
      smoothedPose.keypoints.length;

    return smoothedPose;
  }

  processFrame = throttle(
    async (frame: HTMLVideoElement | ImageData) => {
      if (
        !this.worker ||
        !this.isRunning ||
        !this.isInitialized ||
        !this.isWarmedUp ||
        !frame
      )
        return;

      try {
        // Before anything reads or transfers the frame: is the camera still
        // delivering new ones? A frozen camera stops the scan here.
        if (frame instanceof ImageData) {
          this.observeVideoFrame(alignmentVideoFreeze, frame, "alignment");
          if (!this.isRunning) return;
        }
        this.framesProcessed++;
        // Only run object detection every 4 frames
        const shouldRunObjectDetection = this.framesProcessed % 4 === 0;

        if (shouldRunObjectDetection) {
          // check if the object detector is running
          if (!this.objectDetectionManager.isRunning) {
            this.objectDetectionManager.start();
          }
          // Since processFrame now returns a Promise, we need to await it
          const objDetectorRes = await this.objectDetectionManager.processFrame(
            frame,
            performance.now()
          );

          if (objDetectorRes) {
            this.heightRatio =
              objDetectorRes.personHeight / objDetectorRes.frameHeight;
            this.heightRatioAt = performance.now();
            this.frameHeight = objDetectorRes.frameHeight;
            this.frameWidth = objDetectorRes.frameWidth;
          } else {
            this.heightRatio = 0;
            this.heightRatioAt = performance.now();
            this.frameHeight = 0;
            this.frameWidth = 0;
          }
        }

        // Two assignments, no allocation. MoveNet resizes whatever it is given
        // to 192x192, so this is the downsample factor the keypoints were
        // produced at — `getUserMedia` is called with no resolution constraint,
        // so the frame reaches the model at full native size and nothing has
        // ever recorded what that size is.
        if (frame && (frame as ImageData).width) {
          this.lastFrameWidth = (frame as ImageData).width;
          this.lastFrameHeight = (frame as ImageData).height;
        }

        // Run pose detection on every frame for smoother guidance
        const poses = await this.detectPose(frame);
        const source = this.lastDetectSource;
        // Device path only: on the server path a low score is the network's,
        // and there is nothing further to switch to.
        if (this.lastDetectSource === "device") this.observeBlind(poses);

        if (poses && poses.length > 0) {
          const pose = poses[0]; // Use the first detected pose
          this.handleSettingPose(pose);
          // Calculate guidance based on pose data
          const message = this.calculateGuidance();

          if (message !== undefined) {
            this.emit("guidance", message);
          }
        } else {
          // No poses detected for several frames
          this.emit("guidance", null);
        }

        // Every frame the detector completed — person found or not — and AFTER
        // this frame's `jointCoordinates`. Auto's "phone too slow" counts the
        // phone's frames from here: the joints above need a person box and a
        // pose, so counting them alone read a person at the edge of the frame
        // as a slow detector and moved fast iPhones to the cloud. The person
        // box still gates the joints (the `!frameHeight` early return, which
        // is the latch's dropout signal), never this.
        this.emit("frameDetected", source);
      } catch (error) {
        this.emit("error", error as Error);
      }
    },
    1000 / 15, // Increase to 15 Hz for smoother UI updates
    { trailing: false, leading: true } // Process first frame immediately
  );

  /**
   * The single worker-message dispatcher. Bound once per worker.
   *
   * An arrow property, not a method, so `removeEventListener` could match it
   * and so `this` is the manager regardless of how the event system calls it.
   *
   * Unknown request ids are dropped by the registry rather than thrown: a
   * response can legitimately arrive for a request already failed by timeout,
   * and a throw inside an event listener is an unhandled error, not a rejected
   * promise.
   */
  private handleDetectMessage = (e: MessageEvent): void => {
    const { type, poses, error, requestId } = e.data || {};
    if (typeof requestId !== "number") return;
    if (type === "DETECT_SUCCESS") {
      this.pendingDetects.resolve(requestId, poses);
    } else if (type === "DETECT_ERROR") {
      this.pendingDetects.reject(requestId, new Error(error));
    }
  };


  /** Bound once, like `handleDetectMessage`, so it can be removed. */
  private handleWorkerError = (): void => {
    this.onWorkerError();
  };

  private onWorkerError(): void {
    const wasBroken = detectorHealth.isBroken();
    detectorHealth.recordWorkerCrash();
    this.pendingDetects.clear("Pose worker crashed");
    if (!wasBroken) {
      logWarning("[PoseDetection] detector broken", { reason: "worker-error" });
    }
  }

  /** One warning per attempt when the detector trips — never one per error. */
  private recordDetectFailure(error: unknown): void {
    const wasBroken = detectorHealth.isBroken();
    const timedOut = isDetectTimeout(error);
    if (timedOut) detectorHealth.recordTimeout();
    else detectorHealth.recordError();
    if (!wasBroken && detectorHealth.isBroken()) {
      logWarning("[PoseDetection] detector broken", {
        reason: timedOut ? "timeout" : "errors",
        errors: detectorHealth.errors(),
      });
    }
  }

  /** The device path's pose, as the blind-while-visible tracker needs it. */
  private observeBlind(poses: Pose[] | undefined): void {
    const pose = poses && poses.length ? poses[0] : null;
    let gatedScores: number[] | null = null;
    if (pose) {
      gatedScores = [];
      for (let i = 0; i < JOINTS_OF_INTEREST.length; i++) {
        const kp = pose.keypoints.find((k) => k.name === JOINTS_OF_INTEREST[i]);
        gatedScores.push(kp && typeof kp.score === "number" ? kp.score : 0);
      }
    }
    blindWhileVisible.observe({
      now: performance.now(),
      heightRatio: this.heightRatio,
      heightRatioAt: this.heightRatioAt,
      gatedScores,
    });
  }

  /**
   * One detect, matched to its response by id.
   *
   * Callers are unchanged: this still resolves `Pose[]`. What changed is that
   * with two detects in flight — `detectRotationPose` is fire-and-forget from a
   * fixed 250 ms interval, so any device slower than that has two — each promise
   * now settles with ITS OWN response instead of whichever arrived first.
   */
  private async detectPose(
    image: HTMLVideoElement | ImageData,
    opts?: { tick?: number }
  ): Promise<Pose[]> {
    // Server-side pose. `shouldUseServer()` is false unless an endpoint is
    // configured, the Firestore kill switch is on, AND the router picked
    // `server` for this attempt — so for everyone else the worker path below is
    // reached by the same call, in the same tick, as before this branch existed.
    //
    // `HTMLVideoElement` is not routed: only `ImageData` can be encoded off the
    // canvas cheaply, and both production callers already pass `ImageData`
    // (`useProcessVideoFrame`, `CountdownManager`). A video element falls
    // through to the worker rather than silently doing nothing.
    if (image instanceof ImageData && serverPoseService.shouldUseServer()) {
      this.lastDetectSource = "server";
      return serverPoseService.detect(image, opts);
    }
    this.lastDetectSource = "device";
    return this.detectOnDevice(image);
  }

  /**
   * The worker path on its own, so the startup benchmark measures THIS handset
   * even when the router would send a frame to the server.
   *
   * @param extra.fresh reset + pad + unpad in the worker — the pose server's
   *   procedure, for detects compared against the server's reference.
   * @param extra.boot a detect that runs before any scan (the warm-up, the
   *   startup check). It gets `BOOT_DETECT_TIMEOUT_MS`, and its outcome never
   *   reaches `detectorHealth`: that signal describes a scan attempt, and a
   *   cold first detection is not evidence the detector broke.
   */
  private detectOnDevice(
    image: HTMLVideoElement | ImageData,
    extra?: { fresh?: boolean; boot?: boolean }
  ): Promise<Pose[]> {
    const worker = this.worker;
    if (!worker) {
      return Promise.reject(new Error("Worker not initialized"));
    }

    const boot = !!(extra && extra.boot);
    const { requestId, promise } = this.pendingDetects.create(
      boot ? BOOT_DETECT_TIMEOUT_MS : DETECT_TIMEOUT_MS
    );
    const fresh = !!(extra && extra.fresh);

    const post = () => {
      if (image instanceof ImageData) {
        const buffer = image.data.buffer;
        worker.postMessage(
          { type: "DETECT", payload: { image, requestId, fresh } },
          [buffer]
        );
      } else {
        worker.postMessage({ type: "DETECT", payload: { image, requestId, fresh } });
      }
    };
    const slowGate = devSlowFirstDetectGate();
    if (slowGate) void slowGate.then(post);
    else post();

    if (boot) return promise;
    return promise.then(
      (poses) => {
        detectorHealth.recordSuccess();
        return poses;
      },
      (error) => {
        this.recordDetectFailure(error);
        throw error;
      }
    );
  }

  /**
   * A spin begins (`CountdownManager.startCountdown`). Replies from any earlier
   * spin — one that failed, or one abandoned mid-way — no longer match.
   */
  beginRotationRun(): void {
    this.rotationRun++;
    rotationVideoFreeze.reset();
  }

  /**
   * A spin failed (`RotationManager.pushError`). Its replies still in flight
   * no longer match, even before the next spin begins. On the cloud each is in
   * flight for 200-500 ms: on 2026-09-14 a failed spin's tick-42 reply landed
   * after the reset, took the next spin's upload slot 20, and the backend
   * rejected that scan on it. Not called on success — a successful spin's late
   * replies still deliver the frames its upload is waiting for.
   */
  closeRotationRun(): void {
    this.rotationRun++;
  }

  getRotationRun(): number {
    return this.rotationRun;
  }

  /**
   * Watch the frames the scan analyses, and stop on a frozen camera.
   *
   * On 2026-09-14 an Android camera froze and the scan analysed one still
   * image for two minutes: the countdown ran, the spin "failed", and nothing
   * said the camera had stopped. Now the first freeze in an attempt or a spin
   * is logged once, with what the camera track reported at that moment, and
   * `cameraFrozen` tells `RotationManager` to cancel the scan. During alignment
   * the loop pauses too, or its guidance and posture events would clear the
   * error screen at once; the spin is already paused by the countdown.
   */
  private observeVideoFrame(
    watch: VideoFreezeWatch,
    image: ImageData,
    phase: "alignment" | "rotation"
  ): void {
    watch.observe(performance.now(), frameSignature(image));
    if (!watch.takeNewFreeze()) return;
    const frozenMs = watch.frozenMs();
    logWarning("[Camera] video frozen", {
      phase,
      frozenMs,
      source: this.lastDetectSource,
      ...cameraTrackState(),
    });
    if (phase === "alignment") this.pause();
    this.emit("cameraFrozen", { phase, frozenMs });
  }

  detectRotationPose(image: ImageData, ticksCount: number): void {
    this.observeVideoFrame(rotationVideoFreeze, image, "rotation");
    // Create a true deep copy of the ImageData BEFORE the buffer is transferred
    // This is critical because detectPose transfers the buffer to the worker
    const clonedFrame = new ImageData(
      new Uint8ClampedArray(image.data.slice()), // slice() creates a true copy
      image.width,
      image.height
    );
    // The tick travels with the request and is echoed by the server. The
    // rotation FSM pairs a pose with a frame by tick, and a pose misattributed
    // by one tick is what produces spurious `under-rotation` /
    // `did-not-rotate-in-place` — the same failure the worker's request ids fix.
    // The run is read NOW, at send time. Rotation detects are fire-and-forget,
    // so the reply can land after this spin failed and the next one began;
    // `RotationManager` drops replies whose run is not current.
    const run = this.rotationRun;
    this.detectPose(image, { tick: ticksCount }).then((poses) => {
      // send an event to the rotation manager
      this.emit("rotationPose", {
        poses,
        frame: clonedFrame,
        ticksCount,
        run,
      });
    });
  }

  private keyPointInFrame(keypoint: Keypoint | undefined): boolean {
    if (!keypoint) return false;
    const threshold = 0.3; // TODO: Define a proper threshold
    return (
      keypoint.x > 0 && keypoint.y > 0 && (keypoint.score || 0) > threshold
    );
  }

  private getJointBoundary(jointName: string): Boundary | undefined {
    return boundaries.find((b) => b.name === jointName);
  }

  /**
   * Inflation happens in REFERENCE space, before scaling, so the same relative
   * box survives an orientation change or a canvas resize. The returned rect is
   * both the one the gate tests against and the one `ScanCanvas` draws
   * (`area.boundary`), so the user always sees the target they are actually
   * being held to. Never test against a box the user cannot see.
   */
  private calculateScaledBoundary(
    boundary: Boundary,
    scaleX: number,
    scaleY: number
  ): Boundary {
    // 0 on the standard path, so every term below is a multiplication by zero
    // and this returns the identical rect it returned before BUG-0008.
    const ratio = this.tuning.boxInflateRatio;
    const dx = (boundary.maxX - boundary.minX) * ratio;
    const dy = (boundary.maxY - boundary.minY) * ratio;
    const inflate = boundary.inflate;
    return {
      ...boundary,
      minX: (boundary.minX - dx * inflate.minX) * scaleX,
      maxX: (boundary.maxX + dx * inflate.maxX) * scaleX,
      minY: (boundary.minY - dy * inflate.minY) * scaleY,
      maxY: (boundary.maxY + dy * inflate.maxY) * scaleY,
    };
  }

  private isPointInBoundary(x: number, y: number, boundary: Boundary): boolean {
    return (
      x >= boundary.minX &&
      x <= boundary.maxX &&
      y >= boundary.minY &&
      y <= boundary.maxY
    );
  }

  private getJointArea(
    jointName: string,
    jointX: number,
    jointY: number,
    scaleX: number,
    scaleY: number
  ): JointArea | null {
    const boundary = this.getJointBoundary(jointName);
    if (!boundary) return null;

    const scaledBoundary = this.calculateScaledBoundary(
      boundary,
      scaleX,
      scaleY
    );
    const isInArea = this.isPointInBoundary(jointX, jointY, scaledBoundary);

    // Did the inflation do the work, or would this joint have passed anyway?
    // Bounds are compared inline rather than through a second rect object —
    // this runs four times per emit.
    let rescuedByInflation = false;
    if (isInArea && this.tuning.boxInflateRatio > 0) {
      const strictMinX = boundary.minX * scaleX;
      const strictMaxX = boundary.maxX * scaleX;
      const strictMinY = boundary.minY * scaleY;
      const strictMaxY = boundary.maxY * scaleY;
      rescuedByInflation = !(
        jointX >= strictMinX &&
        jointX <= strictMaxX &&
        jointY >= strictMinY &&
        jointY <= strictMaxY
      );
    }

    return {
      boundary: scaledBoundary,
      center: {
        x: (scaledBoundary.minX + scaledBoundary.maxX) / 2,
        y: (scaledBoundary.minY + scaledBoundary.maxY) / 2,
      },
      isJointInArea: isInArea,
      imageSrc: isInArea ? boundary.src : boundary.src2,
      rescuedByInflation,
    };
  }

  /** Tuning is per device class; `STANDARD_TUNING` is today's behaviour. */
  setTuning(tuning: AlignmentTuning): void {
    this.tuning = tuning;
  }

  getTuning(): AlignmentTuning {
    return this.tuning;
  }

  /** True once today's exact gate has been satisfied in this attempt. */
  hasSeenStrictPosture(): boolean {
    return this.strictPostureSeen;
  }

  /**
   * Which source detected the latest frame. Read by the alignment accumulator,
   * so Auto's "phone too slow" counts only frames the phone detected.
   */
  getLastDetectSource(): "device" | "server" {
    return this.lastDetectSource;
  }

  /** Emits a shadow latch would have converted. See `latchRescuedEmits`. */
  getLatchRescuedEmits(): number {
    return this.latchRescuedEmits;
  }

  /**
   * Server-pose counters for the summary rows.
   *
   * Read THROUGH the manager rather than by the accumulator importing
   * `serverPoseService` directly: that service reads `getRecentPoseHz` from
   * `alignmentAccumulator`, so a direct import would close a module cycle
   * (accumulator -> service -> accumulator) whose bindings are undefined at
   * init time under ES module semantics. The manager is already the
   * accumulator's structurally-typed dependency for exactly this kind of read.
   */
  getServerPoseStats(): Record<string, unknown> {
    // Spread into a fresh object: the service returns a typed struct, and the
    // accumulator's structural dependency is declared as an index signature so
    // it stays agnostic about which fields exist.
    return { ...serverPoseService.getStats() };
  }

  /**
   * A LATCHED observation is remembered for up to `jointLatchMs`, so it is held
   * to a stricter standard than the instantaneous one: it also requires
   * `isDetected`. Today's `areasInPosition` ignores keypoint confidence, which
   * is tolerable when the value is re-checked 15x a second and is not when it
   * is remembered for half a second.
   */
  private stampJointArea(
    jointName: string,
    now: number,
    isJointInArea: boolean,
    isDetected: boolean
  ): void {
    if (isJointInArea && isDetected) {
      this.lastInAreaAt[jointName] = now;
    }
  }

  /**
   * @param latchMs defaults to the tuning in force. Passed explicitly only by
   *   the SHADOW counter, which asks the same question of a latch that is not
   *   applied — see `latchRescuedEmits`.
   */
  private allJointsLatched(
    now: number,
    latchMs: number = this.tuning.jointLatchMs
  ): boolean {
    for (let i = 0; i < JOINTS_OF_INTEREST.length; i++) {
      const stamp = this.lastInAreaAt[JOINTS_OF_INTEREST[i]];
      if (stamp === undefined || now - stamp > latchMs) return false;
    }
    return true;
  }

  /**
   * Pose loss, pause, or a new alignment attempt. Without this a user who walks
   * out of frame carries four fresh stamps into an empty frame and the gate
   * fires on nobody.
   */
  private clearJointLatch(): void {
    this.lastInAreaAt = {};
  }

  private emitJointCoordinates(pose: Pose): void {
    const { keypoints } = pose;
    const now = performance.now();

    const jointCoordinates: JointCoordinates = {};

    // MUST precede the dropout check below. This early return fires whenever
    // the EfficientDet pass found no person box (`frameHeight` is zeroed at
    // processFrame's else-branch), and in that state no coordinates are emitted
    // at all. Updating `lastEmitAt` above it would mean a minute of "we cannot
    // see anybody" registers as a minute of healthy emits, and stale latch
    // stamps would survive it — the exact case the dropout check exists for.
    //
    // ⚠️ This guard is LOAD-BEARING FOR THE LATCH, not only for drawing. See
    // HERE_BE_DRAGONS.md § "The `!frameHeight` early return is the latch's
    // dropout signal" before touching it — BUG-0009's dossier describes
    // removing it as a local fix, and it is not.
    if (!this.canvasDimensions || !this.frameHeight || !this.frameWidth) return;

    // A gap this long means we could not SEE the joints, not that they moved.
    // Carrying stamps across it would let the gate fire on an empty frame.
    if (this.lastEmitAt !== null && now - this.lastEmitAt > POSE_LOST_GAP_MS) {
      this.clearJointLatch();
    }
    this.lastEmitAt = now;

    const { width, height } = this.canvasDimensions;
    const scaleX = width / this.frameWidth;
    const scaleY = height / this.frameHeight;
    const boundaryScaleX = width / 1024;
    const boundaryScaleY = height / 1336;
    const areasInPosition = [false, false, false, false];

    JOINTS_OF_INTEREST.forEach((jointName, index) => {
      const keypoint = keypoints.find((kp) => kp.name === jointName);
      const isDetected = this.keyPointInFrame(keypoint);

      if (keypoint) {
        const jointX = keypoint.x * scaleX;
        const jointY = keypoint.y * scaleY;
        const area = this.getJointArea(
          jointName,
          jointX,
          jointY,
          boundaryScaleX,
          boundaryScaleY
        );
        areasInPosition[index] = area?.isJointInArea || false;
        this.stampJointArea(jointName, now, areasInPosition[index], isDetected);

        jointCoordinates[jointName] = {
          x: jointX,
          y: jointY,
          isDetected,
          // Telemetry only. `isDetected` already collapses this to a boolean at
          // 0.3, which throws away exactly the distinction the prod question
          // needs: a joint at 0.05 and one at 0.29 are both "undetected" and
          // are not the same finding.
          score: keypoint.score,
          area,
        };
      } else {
        jointCoordinates[jointName] = null;
      }
    });

    // The gate is a UNION, not a replacement. `jointLatchMs > 0` is false on the
    // standard path, so this reduces to today's `every()` exactly — and no
    // tuning value can ever make a user who passes today fail, because the
    // relaxed term can only ADD.
    const strictPosture = areasInPosition.every((inPosition) => inPosition);
    const latchedPosture =
      this.tuning.jointLatchMs > 0 && this.allJointsLatched(now);

    if (strictPosture) this.strictPostureSeen = true;
    // MEASUREMENT ONLY — deliberately below the gate expression and with no
    // effect on it. `SHADOW_LATCH_MS` is never read by `latchedPosture`, which
    // uses `tuning.jointLatchMs` (0 on every path in this release), so the emit
    // on the next line is bit-identical to 2.5.13.
    else if (this.allJointsLatched(now, SHADOW_LATCH_MS)) {
      this.latchRescuedEmits++;
    }

    this.emit("correctPosture", strictPosture || latchedPosture);
    this.emit("jointCoordinates", jointCoordinates);
  }

  private calculateGuidance = throttle(
    (): GuidanceMessage => {
      const smoothedPose = this.pose;
      const heightRatio = this.heightRatio;
      const frameHeight = this.frameHeight;

      if (!smoothedPose || !heightRatio || !frameHeight) {
        // Added frameHeight check for safety
        return undefined;
      }

      const { keypoints } = smoothedPose;

      // --- Condition Calculations ---
      const isTooClose = heightRatio > 0.95;

      const rightShoulder = keypoints.find(
        (kp) => kp.name === "right_shoulder"
      );
      const leftShoulder = keypoints.find((kp) => kp.name === "left_shoulder");
      const rightAnkle = keypoints.find((kp) => kp.name === "right_ankle");
      const leftAnkle = keypoints.find((kp) => kp.name === "left_ankle");
      const rightEye = keypoints.find((kp) => kp.name === "right_eye");
      const leftEye = keypoints.find((kp) => kp.name === "left_eye");
      const rightWrist = keypoints.find((kp) => kp.name === "right_wrist");
      const leftWrist = keypoints.find((kp) => kp.name === "left_wrist");

      const shouldersInView =
        this.keyPointInFrame(rightShoulder) &&
        this.keyPointInFrame(leftShoulder);
      const anklesInView =
        this.keyPointInFrame(rightAnkle) && this.keyPointInFrame(leftAnkle);
      const eyesInView =
        this.keyPointInFrame(rightEye) && this.keyPointInFrame(leftEye);
      const wristsInView =
        this.keyPointInFrame(rightWrist) && this.keyPointInFrame(leftWrist);

      const isTooFar = shouldersInView && anklesInView && heightRatio <= 0.6;

      const eyesYPosition = Math.min(rightEye?.y || 0, leftEye?.y || 0);
      const eyesYPositionRatio = eyesYPosition / frameHeight;
      const needsTiltDown = eyesInView && eyesYPositionRatio > 0.25;

      const anklesYPosition = Math.max(
        rightAnkle?.y || Infinity,
        leftAnkle?.y || Infinity
      );
      const anklesYPositionRatio = anklesYPosition / frameHeight;
      const needsTiltUp = anklesInView && anklesYPositionRatio < 0.75;

      const allGood =
        eyesInView && shouldersInView && anklesInView && wristsInView;

      // --- Priority Logic ---
      if (isTooClose) {
        return "Move Back";
      } else if (isTooFar) {
        return "Move Closer";
      } else if (needsTiltDown) {
        return "Tilt Device Down";
      } else if (needsTiltUp) {
        return "Tilt Device Up";
      } else if (allGood) {
        // Only return 'null' (all good) if no other corrective guidance is needed.
        return null;
      } else {
        // If not 'allGood' but no specific corrective action identified by the priorities above.
        return undefined;
      }
    },
    1000 / 2, // Run twice a second
    { trailing: false }
  );

  private resetToDefaults(): void {
    this.heightRatio = 0;
    this.heightRatioAt = null;
    this.frameHeight = 0;
    // Was a duplicated `frameHeight = 0`, so `frameWidth` survived a pause with
    // a stale value from the previous attempt.
    this.frameWidth = 0;
    this.framesProcessed = 0;
    this.pose = null;
    this.posesBuffer = [];
    this.clearJointLatch();
    this.lastEmitAt = null;
    // `strictPostureSeen` is deliberately NOT cleared here. `pause()` calls this
    // method, and `CountdownManager.startCountdown()` calls `pause()` one step
    // before flipping the status to "rotating" — which is what makes
    // `useScanPhases` exit ALIGNMENT and emit `scan.alignment.summary`. Clearing
    // it here therefore guarantees `strictPostureSeen: false` on every
    // successful scan, which is the exact inverse of what the field is for.
    // It is cleared in `start()` instead, the real attempt boundary.
  }

  isReady(): boolean {
    return (
      this.isInitialized &&
      this.isWarmedUp &&
      this.worker !== null &&
      this.objectDetectionWarmedUp
    );
  }

  /**
   * Clear the per-attempt telemetry counters without touching detection.
   *
   * `start()` is not the only attempt boundary. `src/telemetry/phases.ts` closes
   * ALIGNMENT on `visibilitychange -> hidden` and re-enters it with
   * `attempt + 1` on return (`resumeFromBackground`), and nothing on that path
   * calls `start()` — so attempt 2 inherited attempt 1's `strictPostureSeen`.
   * That makes the audit query `EVENTS.md` documents —
   * `strictPostureSeen: false` alongside a `scan.rotation.summary` in the same
   * journey — silently exclude the wrong-avatar case it was built to catch, on
   * exactly the backgrounded sessions the struggle record exists for.
   *
   * `alignmentAccumulator` calls this at every ALIGNMENT entry, which is the
   * boundary the summary rows are keyed on.
   */
  resetAttemptMetrics(): void {
    this.strictPostureSeen = false;
    this.latchRescuedEmits = 0;
    // Both describe ONE attempt, and `serverPoseService.resetAttempt` below
    // reads them through the router, so they reset first.
    detectorHealth.reset();
    blindWhileVisible.reset();
    phoneTooSlow.reset();
    jointUnseenWhileFramed.reset();
    alignmentVideoFreeze.reset();
    // Same lifecycle: the RTT sample, the request counts and the flap budget
    // describe ONE attempt. Without this a retry inherits the first attempt's
    // network conditions and its spent switch budget.
    serverPoseService.resetAttempt();
  }

  start(): void {
    // Before the readiness guard on purpose: a restart attempted while the
    // manager is not ready must still not carry the previous attempt's flag
    // into the next alignment summary. Both callers of `start()` guard on
    // `isRunning`, so this only ever runs at a genuine attempt boundary.
    this.resetAttemptMetrics();

    if (this.isReady()) {
      this.isRunning = true;
      // Dev proof (spec §6): a detector that dies mid-alignment. The next
      // detect times out and DetectorHealth trips. Ignored in prod builds.
      const killAfterMs = Number(devFlag("killDetectorAfterMs"));
      if (killAfterMs > 0 && this.worker && this.killDetectorTimer === null) {
        this.killDetectorTimer = setTimeout(() => {
          if (this.worker) this.worker.terminate();
        }, killAfterMs);
      }
    }
  }

  pause(): void {
    this.isRunning = false;
    this.resetToDefaults();
  }

  terminate(): void {
    if (this.worker) {
      this.worker.removeEventListener("message", this.handleDetectMessage);
      this.worker.removeEventListener("error", this.handleWorkerError);
      this.worker.postMessage({ type: "DISPOSE" });
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingDetects.clear("Pose worker was terminated");
    this.isRunning = false;
    this.isInitialized = false;
    this.isWarmedUp = false;
    this.resetToDefaults();
    this.removeAllListeners();
  }

  /**
   * The detected person's height as a fraction of the frame height, from
   * EfficientDet (updated every 4th processed frame).
   *
   * This is the measurement BEHIND the guidance, not the guidance itself:
   * `isTooClose` is `heightRatio > 0.95` and `isTooFar` is `<= 0.6`. Exposed so
   * telemetry can record what the app SAW, not only what it SAID — a user who
   * is told "Move Back" and complies still has a problem if the number never
   * drops, and the guidance histogram alone cannot tell those apart.
   * See `src/telemetry/alignmentAccumulator.ts`.
   */
  getHeightRatio(): number {
    return this.heightRatio;
  }

  /** `null` = not reported (non-webgl backend, or the read threw). */
  getFloat32Enabled(): boolean | null {
    return this.float32Enabled;
  }

  setCanvasDimensions(dimensions: { width: number; height: number }): void {
    this.canvasDimensions = dimensions;
  }
}
